// src/pages/GameWrapper.jsx
import { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useGameFlow } from '../contexts/GameFlowProvider';
import { AnimatePresence, motion as Motion } from 'framer-motion';
import TransitionCurtain from '../components/shared/TransitionCurtain';
import SessionHelpButton from '../components/session-help/SessionHelpButton';
import DevResetButton from '../components/shared/DevResetButton';
import { setSessionContext } from '@tasted/utils/analytics';
import { playerStorage } from '../utils/playerStorage';
import { stepTypeToRoute } from '../lib/stepRoute';
import { supabase } from '@tasted/utils/supabase';
import * as Sentry from '@sentry/react';
import {
  setCurtainVisible,
  MIN_CURTAIN_MS,
  PASS_THROUGH_HOLD_MAX_MS,
  isCurtainPassThrough,
  curtainStepFor,
  isSetupStep,
  continuesSetupRun,
  isRealRejoin,
  markRoomSeen,
} from '../lib/curtainState';
import { useChromeTheme, CHROME } from '../hooks/useChromeTheme';
import { DUR, EASE_BEZIER } from '../lib/anims';
import { dismissActiveEmotes } from '../components/shared/emoteSystem.jsx';
import { useAvatarClipSummary } from '../lib/avatarClipStats';
import { usePodiumCheckoutOpen } from '../lib/podiumCheckout';

const __DEV__ = import.meta.env.DEV;
const debugLog = __DEV__ ? console.log.bind(console) : () => {};
const debugWarn = __DEV__ ? console.warn.bind(console) : () => {};

export default function GameWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const [appLoaderVisible, setAppLoaderVisible] = useState(true);

  const transitionCounterRef = useRef(0);
  const lastPathRef = useRef(location.pathname + location.search);
  const lastNavigateRef = useRef({ path: null, ts: 0, id: 0 });
  const navDelayTimeoutRef = useRef(null);
  const pendingNavRef = useRef({ path: null, id: 0 });
  const prevPrefetchRef = useRef(false);
  const prefetchEndedAtRef = useRef(null);
  const {
    flowSteps,
    sessionDataLoaded,
    isHydrating,
    stepIndex,
    currentStep,
    gameType,
    sessionCode,
    wineList,
    hideWineDetails,
    isPrefetchingNext,
    endPrefetch,
    isResuming,
    resumedStepIndex,
    isGm,
    advanceToNextStep,
    screenReady,
    prefetchProgress,
    currentQuestion,
    wineIndex,
    gameMode,
    tvModeEnabled,
    gmName,
  } = useGameFlow();
  // The box checkout open over the podium on this phone (lib/podiumCheckout):
  // the room's move off the podium waits for it, and goes the moment it
  // closes. Only ever on the podium, so it can hold nothing else.
  const podiumCheckoutOpen = usePodiumCheckoutOpen();
  const podiumMoveHeld = podiumCheckoutOpen && location.pathname === '/game/final-leaderboard';

  // How this phone's character clips loaded, one summary per step (TAS-557).
  useAvatarClipSummary({ surface: 'phone', stepIndex, stepType: currentStep?.type, sessionCode });

  // ── Curtain with a minimum visibility (MIN_CURTAIN_MS) ──────────────────────
  // Curtain breathing room — hold the contextual copy long enough to actually
  // read it, even when the next screen loads instantly. 1500ms flashed by, and
  // the 3.5s that replaced it, repeated on every step, was most of the minute
  // between Join and the first question (UX review, 3 Nov 2026). Now 2s, and a
  // tap lifts it early once the next screen is ready (skipCurtain below).
  // Shared with the screens that anchor their entrance to the curtain lifting.
  const curtainTimerRef = useRef(null);
  const curtainShownAtRef = useRef(null);
  const [curtainActive, setCurtainActive] = useState(false);
  // The curtain portals in above every screen, so it claims the chrome on top of
  // whatever the screen underneath declared — and hands it straight back on exit.
  useChromeTheme(curtainActive ? CHROME.green : null);

  // If this wrapper unmounts mid-curtain, onExitComplete never runs and the
  // singleton would stay visible=true forever — permanently suppressing the
  // powerplay overlay and stranding reveal screens that defer to
  // curtain-exited (codex review 2026-07-14). Reset on unmount.
  useEffect(() => () => {
    setCurtainVisible(false);
    window.dispatchEvent(new CustomEvent('curtain-exited'));
  }, []);

  // Leader info for personalised scoreboard curtain messages.
  // null = still loading; {type:'none'} = nobody has points yet; {type:'tie',
  // names, mine} = shared top score; {type:'leader', name, mine} = one clear
  // leader. QA 2026-07-14: with everyone on 0 the old sorted[0] pick crowned
  // an arbitrary player ("steve is getting ahead" on an all-zero board).
  const [leaderInfo, setLeaderInfo] = useState(null);

  // Pre-fetch leader data as soon as the step becomes 'scoreboard' — before the
  // curtain is visible — so TransitionCurtain never flashes the default fallback copy.
  useEffect(() => {
    if (currentStep?.type !== 'scoreboard' || gameMode !== 'party' || !sessionCode) {
      setLeaderInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [{ data: scores }, { data: players }, { data: answers }] = await Promise.all([
          supabase.from('scores_v2').select('user_id, points, round_id').eq('session_id', sessionCode),
          supabase.from('session_users_v2').select('id, username').eq('session_id', sessionCode),
          supabase.from('answers_v2').select('round_id, created_at').eq('session_id', sessionCode),
        ]);
        if (cancelled) return;
        if (!scores?.length || !players?.length) { setLeaderInfo({ type: 'none' }); return; }

        // Find the latest round_id from answers so we can exclude it from the
        // totals — the curtain should reflect standings *before* this round,
        // not after, so it doesn't spoil the scoreboard reveal.
        const latestRoundId = answers?.length
          ? answers.reduce((a, b) => (new Date(a.created_at) > new Date(b.created_at) ? a : b)).round_id
          : null;

        const totals = {};
        scores.forEach(({ user_id, points, round_id }) => {
          if (round_id === latestRoundId) return;
          totals[user_id] = (totals[user_id] || 0) + (points || 0);
        });

        const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]);
        const topScore = sorted[0]?.[1] ?? 0;
        const currentPlayerId = playerStorage.get();
        // Nobody on the board yet (or all zeros) — no one is "getting ahead".
        if (topScore <= 0) { setLeaderInfo({ type: 'none' }); return; }
        const coLeaders = sorted.filter(([, pts]) => pts === topScore);
        if (coLeaders.length > 1) {
          const names = coLeaders
            .map(([id]) => players.find((p) => p.id === id)?.username)
            .filter(Boolean);
          setLeaderInfo({
            type: 'tie',
            names,
            mine: coLeaders.some(([id]) => id === currentPlayerId),
          });
          return;
        }
        const leader = players.find((p) => p.id === sorted[0][0]);
        if (leader) {
          setLeaderInfo({ type: 'leader', name: leader.username, mine: sorted[0][0] === currentPlayerId });
        } else {
          setLeaderInfo({ type: 'none' });
        }
      } catch {
        // Non-critical — neutral copy beats holding the dots forever.
        if (!cancelled) setLeaderInfo({ type: 'none' });
      }
    })();
    return () => { cancelled = true; };
  }, [currentStep?.type, gameMode, sessionCode]);

  // ── Steps nobody sees (lib/curtainState: isCurtainPassThrough) ─────────────
  // game-rules, the legacy ability step, and game-view-mode when this room will
  // skip it. They used to raise a curtain each, and with no copy of their own
  // each said "One moment…". Now they never get one of their own: the curtain
  // already covering the screen stays up through them and names the real next
  // screen, so the room sees one curtain from the setup screen to the first
  // real step.
  //
  // In a TV-enabled room game-view-mode is a real screen unless the host already
  // picked Phones on the setup screen, which writes a `game-view-mode-set`
  // event with mode 'phone' (GameViewMode then skips itself, like TV-off). The
  // provider's gameViewMode starts at 'phone', so it cannot tell that choice
  // from no choice; this is the same one-row read GameViewMode makes. It runs
  // only in a TV room, on the step before game-view-mode and on it. Until it
  // answers (or if it fails) the step is a real screen, the safe reading.
  const nearViewMode =
    flowSteps?.[stepIndex]?.type === 'game-view-mode' ||
    flowSteps?.[stepIndex + 1]?.type === 'game-view-mode';
  const [phonePresetRead, setPhonePresetRead] = useState({ sessionCode: null, phone: false });
  const phonePreset = phonePresetRead.sessionCode === sessionCode && phonePresetRead.phone;
  useEffect(() => {
    if (!sessionCode || !tvModeEnabled || !nearViewMode) return undefined;
    let disposed = false;
    const settle = (phone) => { if (!disposed) setPhonePresetRead({ sessionCode, phone }); };
    try {
      supabase
        .from('game_events_v2')
        .select('payload')
        .eq('session_id', sessionCode)
        .eq('event_type', 'game-view-mode-set')
        .order('created_at', { ascending: false })
        .limit(1)
        .then(
          ({ data, error }) => settle(!error && data?.[0]?.payload?.mode === 'phone'),
          () => settle(false),
        );
    } catch {
      settle(false);
    }
    return () => { disposed = true; };
  }, [sessionCode, tvModeEnabled, nearViewMode, stepIndex]);
  const passThroughOpts = useMemo(
    () => ({ sessionDataLoaded, tvModeEnabled, phonePreset }),
    [sessionDataLoaded, tvModeEnabled, phonePreset],
  );
  const stepIsPassThrough = isCurtainPassThrough(currentStep, passThroughOpts);
  const curtainTarget = useMemo(
    () => curtainStepFor(flowSteps, stepIndex, passThroughOpts),
    [flowSteps, stepIndex, passThroughOpts],
  );

  // Held, but not forever: a host whose skip stalls gets a Continue button from
  // GameViewMode after 10s, and the curtain must not sit on top of it.
  const [passThroughHoldExpired, setPassThroughHoldExpired] = useState(null);
  useEffect(() => {
    if (!stepIsPassThrough) return undefined;
    const id = setTimeout(() => setPassThroughHoldExpired(stepIndex), PASS_THROUGH_HOLD_MAX_MS);
    return () => clearTimeout(id);
  }, [stepIsPassThrough, stepIndex]);
  const holdingPassThrough = stepIsPassThrough && passThroughHoldExpired !== stepIndex;

  // ── The setup run, for every player but the host (lib/curtainState) ──────
  // On participation-mode, game-view-mode and game-rules a player's screen is
  // the one line "<host> is setting up the game…". A curtain between two of
  // them turned that line green and back, naming steps the player never sees
  // (staging replay, 4 Nov 2026), so moving from one setup step to the next
  // raises no curtain on a player's phone. Entering the run still does, and
  // any curtain up over it says the same line (curtainSetupBy below). Not while
  // hydrating: the room's position is a guess until then. The host's curtains
  // are unchanged.
  const playerInSetupRun = !isGm && !isHydrating && continuesSetupRun(flowSteps, stepIndex);
  const curtainSetupBy = !isGm && isSetupStep(currentStep) ? gmName : null;

  const noGateTypes = ['wine-reveal'];
  // In party mode the question-cycle (question → reveal → reveal-answer) has its own
  // visual choreography — suppress the curtain for those steps so it doesn't double up.
  const partyQuestionCycle = ['reveal', 'reveal-answer'];

  useEffect(() => {
    const allowed =
      !appLoaderVisible &&
      !noGateTypes.includes(currentStep?.type) &&
      !(gameMode === 'party' && partyQuestionCycle.includes(currentStep?.type)) &&
      !playerInSetupRun &&
      // Held on the podium for the box checkout: the room has moved, but this
      // phone has not, so no curtain over the sheet.
      !podiumMoveHeld;
    // isHydrating joins the other two (Mel, testing a fresh join: "we're
    // seeing multiple curtains and loading states, it should just be the
    // one curtain"). Hydration used to paint its own full-screen panel and
    // then hand over to the curtain, so a single join showed the app loader,
    // a recovery screen and a curtain in a row. One green screen now covers
    // the whole wait; only its wording changes underneath.
    const loading = isPrefetchingNext || !screenReady || isHydrating;

    // Don't re-show the curtain if it was already dismissed for this step
    // and the only trigger is isPrefetchingNext (leaving current step).
    // The next step's mount will set screenReady=false which properly
    // triggers the curtain with the new step's content.
    const isLeavingCurrentStep = isPrefetchingNext && screenReady;

    // A pass-through step keeps the curtain up even once its (empty) screen
    // says it is ready: lifting there showed a blank page for a second before
    // the real step's curtain dropped again.
    const needed = allowed && ((loading && !isLeavingCurrentStep) || holdingPassThrough);

    if (needed) {
      if (!curtainShownAtRef.current) {
        curtainShownAtRef.current = Date.now();
      }
      clearTimeout(curtainTimerRef.current);
      curtainTimerRef.current = null;
      setCurtainActive(true);
      setCurtainVisible(true);
    } else if (curtainShownAtRef.current) {
      const elapsed = Date.now() - curtainShownAtRef.current;
      const remaining = Math.max(0, MIN_CURTAIN_MS - elapsed);
      clearTimeout(curtainTimerRef.current);
      curtainTimerRef.current = setTimeout(() => {
        setCurtainActive(false);
        curtainShownAtRef.current = null;
      }, remaining);
    }
    return () => clearTimeout(curtainTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrefetchingNext, screenReady, appLoaderVisible, isHydrating, currentStep?.type, podiumMoveHeld, holdingPassThrough, playerInSetupRun]);

  // A tap on the curtain skips what is left of its floor — but only once the
  // screen underneath is really there. While the next step's pictures are
  // still coming down, or the room's position is still unknown, or the step is
  // one nobody sees, lifting would show a half-loaded or empty screen, so the
  // tap does nothing. The curtain's own "?" is a button, not a skip.
  const canSkipCurtain = screenReady && !isHydrating && !isPrefetchingNext && !holdingPassThrough;
  const skipCurtain = (event) => {
    if (!canSkipCurtain) return;
    if (event?.target?.closest?.('button, a, [role="button"]')) return;
    clearTimeout(curtainTimerRef.current);
    curtainTimerRef.current = null;
    curtainShownAtRef.current = null;
    setCurtainActive(false);
  };

  useEffect(() => {
    if (curtainActive || !screenReady) return;
    window.dispatchEvent(
      new CustomEvent('game-route-visible', {
        detail: { path: location.pathname + location.search },
      })
    );
  }, [curtainActive, screenReady, location.pathname, location.search]);

  // Is this phone coming back to the room, or arriving?
  //
  // The distinction is only a wording one, but it is the difference between
  // telling someone the truth and telling them their connection dropped: a
  // player who has just typed the code is not "getting back to the room" and
  // has no answers to keep safe. A stored seat cannot tell the two apart —
  // every player has one the moment Join succeeds, which is how the normal path
  // came to say "Your answers are safe" (UX review, 3 Nov 2026). So a return is
  // a page opened straight into this game (a reload or a direct link) with a
  // seat stored for it, or a second time this page enters the room (see
  // isRealRejoin). Read once per room, at mount.
  const rejoining = useMemo(
    () => isRealRejoin({ sessionCode, hasStoredSeat: Boolean(playerStorage.get(sessionCode)) }),
    [sessionCode],
  );
  // Once the room's position is known this page has been in the room, so a
  // later GameWrapper for it (the error boundary's Try again) is a return.
  useEffect(() => {
    if (sessionCode && sessionDataLoaded && !isHydrating) markRoomSeen(sessionCode);
  }, [sessionCode, sessionDataLoaded, isHydrating]);

  // Question text for the curtain — use the current question if this step type needs it
  const curtainQuestionText = useMemo(() => {
    const showForTypes = ['question', 'reveal', 'reveal-answer', 'discussion'];
    if (!currentStep || !showForTypes.includes(currentStep.type)) return null;
    return currentQuestion?.text ?? null;
  }, [currentStep, currentQuestion]);

  // What the curtain names. On a pass-through step it looks ahead to the real
  // step, and the current question / wine are not that step's, so it works
  // the wine number out from the step itself and holds the question copy until
  // the question step lands (the party curtain then fades its line in).
  const curtainLooksAhead = stepIsPassThrough && curtainTarget.index !== stepIndex;
  const curtainStep = curtainLooksAhead ? curtainTarget.step : currentStep;
  const curtainWineNumber = useMemo(() => {
    if (curtainLooksAhead && curtainStep?.wine && Array.isArray(wineList)) {
      const i = wineList.indexOf(curtainStep.wine);
      if (i >= 0) return i + 1;
    }
    return wineIndex != null ? wineIndex + 1 : null;
  }, [curtainLooksAhead, curtainStep, wineList, wineIndex]);

  // Track global app loader visibility to avoid stacking loaders
  useEffect(() => {
    function onHidden() {
      setAppLoaderVisible(false);
    }
    if (typeof window !== 'undefined') {
      if (window.__appLoaderHiddenAt) {
        setAppLoaderVisible(false);
      } else {
        window.addEventListener('app-loader-hidden', onHidden, { once: true });
      }
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('app-loader-hidden', onHidden);
      }
    };
  }, []);

  useEffect(() => {
    if (__DEV__) {
      const ts = Date.now();
      console.groupCollapsed(
        '[GW] Debug',
        new Date(ts).toISOString(),
        ts
      );
      console.log('flowSteps:', flowSteps);
      console.log('stepIndex:', stepIndex);
      console.log('currentStep:', currentStep);
      console.log('gameType:', gameType);
      console.log('sessionCode:', sessionCode);
      console.log('wineList:', wineList);
      console.log('hideWineDetails:', hideWineDetails);
      console.log('current path:', location.pathname);
      console.log(
        'isResuming:',
        isResuming,
        'resumedStepIndex:',
        resumedStepIndex
      );
      console.groupEnd();
    }
  }, [
    flowSteps,
    stepIndex,
    currentStep,
    gameType,
    sessionCode,
    wineList,
    hideWineDetails,
    location.pathname,
    location.search,
    isResuming,
    resumedStepIndex,
  ]);

  // Track prefetch overlay visibility transitions for logging and timing
  useEffect(() => {
    const overlayVisible =
      (isPrefetchingNext || !screenReady) &&
      !appLoaderVisible &&
      currentStep?.type !== 'wine-review' &&
      currentStep?.type !== 'wine-reveal';

    // Spinner fade in/out handling
    if (overlayVisible) {
      // no spinner fade implementation here (kept simple), we keep existing overlay
    }
    if (overlayVisible && !prevPrefetchRef.current) {
      const t = Date.now();
      debugLog('🔥 PREFETCH_OVERLAY_SHOWN', {
        time: t,
        iso: new Date(t).toISOString(),
      });
    }
    if (prevPrefetchRef.current && !overlayVisible) {
      const t = Date.now();
      prefetchEndedAtRef.current = t;
      debugLog('🧯 PREFETCH_OVERLAY_HIDDEN', {
        time: t,
        iso: new Date(t).toISOString(),
      });
    }
    prevPrefetchRef.current = overlayVisible;
  }, [isPrefetchingNext, screenReady, appLoaderVisible, currentStep?.type]);

  // Hide overlay when route has changed and first paint completed
  useEffect(() => {
    // When the Outlet content mounts for the new route, ensure we hide the prefetch overlay
    // so it doesn't linger or fade over the previous screen.
    if (isPrefetchingNext) {
      const t = Date.now();
      debugLog('🧯 PREFETCH_FORCE_HIDE_ON_ROUTE', {
        time: t,
        iso: new Date(t).toISOString(),
        path: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search, isPrefetchingNext]);

  // When the wrapped route content finishes its entry animation, ensure we end prefetch
  const handleAnimComplete = () => {
    const t = Date.now();
    debugLog('🏁 OUTLET_ANIM_COMPLETE', {
      path: location.pathname + location.search,
      time: t,
      iso: new Date(t).toISOString(),
    });
    // Hide the prefetch overlay if still visible
    if (isPrefetchingNext) endPrefetch();
  };

  // Log when the location actually changes (route committed)
  useEffect(() => {
    debugLog('📍 ROUTE_CHANGE', {
      to: location.pathname + location.search,
      time: Date.now(),
    });
    // Fade out any playing celebration emote. Overlay emotes are position:fixed
    // and the exiting screen stays mounted through the route transition, so a
    // mid-animation "next" would otherwise leave the emote playing on top of
    // the incoming screen.
    dismissActiveEmotes();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!sessionDataLoaded) {
      debugLog('⏳ Waiting for session data before routing game step');
      return;
    }

    // TAS-374: `stepIndex` is a cache guess until the provider has heard from
    // the server. Routing on it would navigate this phone to wine 1 while the
    // table is on wine 2 — then navigate again a moment later, which is the
    // flicker-then-blank nine players reported in prod session 5017.
    if (isHydrating) {
      debugLog('⏳ Waiting for the room position before routing game step');
      return;
    }

    if (!flowSteps || flowSteps.length === 0) {
      debugWarn('🚨 No flow steps available.');
      return;
    }

    // Note: we no longer pause navigation during prefetch. We keep the overlay on top
    // and end it when the destination has animated in, to avoid "stuck" states.

    // Note: do NOT early-return here. We schedule a delayed navigate below
    // when the overlay just ended (see NAVIGATE_DELAY logic) so the
    // current screen remains visible for 5s before transitioning.

    // If we are in the middle of resuming, wait until the resume index is applied
    if (
      isResuming &&
      typeof resumedStepIndex === 'number' &&
      stepIndex !== resumedStepIndex
    ) {
      return;
    }

    // If we deep-linked to a concrete game page (e.g., Lobby resume pushed us straight
    // to /game/reveal?...) and resume hasn't resolved yet, avoid bouncing to the route
    // for stepIndex 0. Hold navigation until resume sets the proper stepIndex.
    const segments = (location.pathname || '').split('/').filter(Boolean);
    const isGameBase = segments[0] === 'game';
    const hasConcreteSubroute =
      isGameBase && typeof segments[1] === 'string' && segments[1].length > 0;
    const resumeNotResolved =
      isResuming === false &&
      (resumedStepIndex === null || typeof resumedStepIndex === 'undefined');
    if (hasConcreteSubroute && resumeNotResolved && stepIndex === 0) {
      debugLog('⏸️ Holding navigation to avoid mid-resume bounce', {
        currentPath: location.pathname + location.search,
        stepIndex,
        isResuming,
        resumedStepIndex,
      });
      return;
    }

    const step = flowSteps[stepIndex];
    if (!step || !sessionCode) return;

    // TAS-126: shared step-type→route map (lib/stepRoute.js), used by both the
    // GM here and followers in useGameEvents so the two can't drift.
    const path = stepTypeToRoute(step, { sessionCode, gameType, hideWineDetails, wineList });
    if (!path) {
      // Pass-through step (game-rules, legacy 'ability') or unknown — GM skips.
      if (isGm) advanceToNextStep();
      return;
    }

    const current = location.pathname + location.search;
    if (current === path) {
      debugLog('✅ Path already correct; no navigation needed:', {
        current,
      });
      return;
    }
    // A player in the box checkout on the podium is not pulled out of it by
    // the room moving on (the GM's "Take everyone on"). Only on the podium,
    // and only while the sheet is open: closing it re-runs this effect, which
    // takes the phone where the room is.
    if (podiumMoveHeld) {
      debugLog('⏸️ ROOM_MOVE_HELD_FOR_CHECKOUT', { from: current, to: path });
      return;
    }
    const now = Date.now();
    const last = lastNavigateRef.current;
    if (last.path === path && now - last.ts < 500) {
      debugLog('⏭️ NAVIGATE_SKIP_DUP', {
        path,
        sinceLastMs: now - last.ts,
        lastId: last.id,
      });
      return;
    }
    // If we just hid the prefetch overlay, delay navigation by 0.5s
    const justEndedPrefetch =
      prefetchEndedAtRef.current && now - prefetchEndedAtRef.current < 500;

    if (justEndedPrefetch) {
      // Avoid scheduling duplicate delayed navigations for the same path
      if (pendingNavRef.current.path === path && navDelayTimeoutRef.current) {
        debugLog('⏭️ NAV_DELAY_SKIPPED_DUP', { path, time: now });
        return;
      }

      const plannedAt = Date.now();
      const transitionId = ++transitionCounterRef.current;
      pendingNavRef.current = { path, id: transitionId };
      debugLog('⏳ NAVIGATE_DELAY_START', {
        transitionId,
        from: current,
        to: path,
        delayMs: 500,
        plannedAt,
        iso: new Date(plannedAt).toISOString(),
      });

      if (navDelayTimeoutRef.current) clearTimeout(navDelayTimeoutRef.current);
      navDelayTimeoutRef.current = setTimeout(() => {
        const fireAt = Date.now();
        debugLog('🚀 NAVIGATE_DELAY_END', {
          transitionId,
          to: path,
          firedAfterMs: fireAt - plannedAt,
          fireAt,
          iso: new Date(fireAt).toISOString(),
        });
        lastPathRef.current = path;
        lastNavigateRef.current = { path, ts: fireAt, id: transitionId };
        navigate(path, { replace: true });
        // Reset pending
        pendingNavRef.current = { path: null, id: 0 };
        navDelayTimeoutRef.current = null;
      }, 500);
    } else {
      const transitionId = ++transitionCounterRef.current;
      debugLog('🔀 NAVIGATE_START', {
        transitionId,
        from: current,
        to: path,
        stepIndex,
        isResuming,
        resumedStepIndex,
        time: now,
        iso: new Date(now).toISOString(),
      });
      lastPathRef.current = path;
      lastNavigateRef.current = { path, ts: now, id: transitionId };
      navigate(path, { replace: true });
    }
  }, [
    stepIndex,
    flowSteps,
    sessionCode,
    navigate,
    gameType,
    wineList,
    hideWineDetails,
    location.pathname,
    location.search,
    isResuming,
    resumedStepIndex,
    isPrefetchingNext,
    currentStep?.type,
    sessionDataLoaded,
    isHydrating,
    // TAS-126: used in the null-path (pass-through) branch. advanceToNextStep's
    // identity tracks stepIndex/flowSteps/wineList (already deps), so this adds
    // no extra runs; isGm is stable state.
    isGm,
    advanceToNextStep,
    podiumMoveHeld,
  ]);

  // If overlay is showing for too long (unexpected), force end with a timeout to avoid being stuck
  useEffect(() => {
    if (!isPrefetchingNext) return;
    const path = location.pathname + location.search;
    const startedAt = Date.now();
    debugLog('[GW] PREFETCH_WATCH_START', {
      path,
      time: startedAt,
      iso: new Date(startedAt).toISOString(),
    });
    const timeoutId = setTimeout(() => {
      debugWarn('[GW] PREFETCH_TIMEOUT_END', {
        path,
        waitedMs: Date.now() - startedAt,
      });
      endPrefetch();
    }, 8000);
    return () => {
      clearTimeout(timeoutId);
      const ended = Date.now();
      debugLog('[GW] PREFETCH_WATCH_END', {
        path,
        durationMs: ended - startedAt,
      });
    };
  }, [isPrefetchingNext, location.pathname, location.search, endPrefetch]);

  // End overlay on step change — wait long enough for the exit animation (1.25s) to finish
  // so the old page's notification overlay doesn't flash through while fading out.
  // handleAnimComplete and the 8s safety timeout are the primary mechanisms for ending prefetch;
  // this is a secondary fallback.
  useEffect(() => {
    if (!isPrefetchingNext) return;
    debugLog('[GW] PREFETCH_END_ON_STEP_CHANGE', { stepIndex });
    const id = setTimeout(() => endPrefetch(), 1500);
    return () => clearTimeout(id);
  }, [stepIndex, isPrefetchingNext, endPrefetch]);

  // Set analytics session context (sessionCode, wines, userId)
  useEffect(() => {
    if (!sessionCode || !Array.isArray(wineList) || wineList.length === 0)
      return;
    const userId = playerStorage.get() || null;
    setSessionContext({ sessionCode, wines: wineList, userId });
  }, [sessionCode, wineList]);

  // Use green background during transitions to prevent flash.
  // 'reveal' must be here (not just 'reveal-answer'): the reveal screen is a green
  // overlay, but the wrapper paints its own background behind the outlet crossfade.
  // Without 'reveal', the off-white bg-wine-background shows through between the
  // fading-out question screen and the fading-in reveal screen — a white flash.
  const greenTypes = ['reveal', 'reveal-answer', 'wine-review', 'final-leaderboard'];
  const useGreenBackground = greenTypes.includes(currentStep?.type);

  // Curtain is driven entirely by isPrefetchingNext / screenReady — no extra overlay state needed

  const motionKey = location.key || location.pathname; // reduces duplicate animations
  const shouldAnimate =
    sessionDataLoaded &&
    Array.isArray(flowSteps) &&
    flowSteps.length > 0 &&
    // Keep overlay above, but still animate the outlet underneath
    // so we can end prefetch on animation complete
    (!isResuming ||
      (typeof resumedStepIndex === 'number' && stepIndex === resumedStepIndex));

  return (
    <div className={`relative min-h-[100dvh] overflow-hidden ${useGreenBackground ? 'bg-wine-primary' : 'bg-wine-background'}`}>
      <DevResetButton />
      {/* Contextual transition curtain — slides down to cover while loading, up
          to reveal. Portalled to <body> so it can't be trapped/clipped: a
          `position: fixed` element is only viewport-relative when NO ancestor
          establishes a containing block, but the route-transition Motion.div
          (App.jsx) and other framer-motion ancestors apply transform/will-change
          during animation, which would otherwise re-anchor + clip the curtain
          (the "green banner clipped to a corner" bug). The portal keeps it in
          the React tree (props/context intact) while escaping every ancestor. */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence onExitComplete={() => { setCurtainVisible(false); window.dispatchEvent(new CustomEvent('curtain-exited')); }}>
          {curtainActive && (
            <Motion.div
              key="transition-curtain"
              initial={{ y: '-100%' }}
              animate={{ y: 0, transition: { duration: DUR.slow * 0.76, ease: EASE_BEZIER.softOut } }}
              exit={{ y: '-100%', transition: { duration: DUR.slow * 0.88, ease: EASE_BEZIER.softOut } }}
              className="fixed inset-0 z-[10000] bg-wine-primary"
              style={{ willChange: 'transform' }}
              data-testid="transition-curtain"
              data-skippable={canSkipCurtain ? 'true' : 'false'}
              onClick={skipCurtain}
            >
              <TransitionCurtain
                // On a pass-through step, the real step after it.
                step={curtainStep}
                questionText={curtainLooksAhead ? null : curtainQuestionText}
                wineNumber={curtainWineNumber}
                leaderInfo={leaderInfo}
                progress={prefetchProgress}
                // While hydrating, `currentStep` is a cache guess (TAS-374),
                // so the curtain would announce a step this phone has not been
                // told it is on. It says what is actually happening instead.
                hydrating={isHydrating}
                rejoining={rejoining}
                // A player on a setup step: the curtain says what their screen
                // says, "<host> is setting up the game…".
                setupBy={curtainSetupBy}
              />
              {/* The curtain covers the screen's header "?", so it carries one
                  (hidden on the TV and wherever help isn't available). */}
              <SessionHelpButton floating onCurtain />
            </Motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {/* TAS-374: the honest screen for "we do not know where the room is yet".
          It replaces the Outlet rather than covering it, so no game screen
          mounts on a step we are only guessing at — a question screen that
          mounts and is then yanked to another step is how an unsubmitted answer
          gets thrown away. Deliberately static: this is a recovery state, and
          decorative motion here would need a liteMotion() gate to earn nothing. */}
      {isHydrating ? (
        // Still replaces the Outlet rather than covering it — no game screen
        // may mount on a step we are only guessing at (TAS-374, prod 5017).
        // The curtain above carries the words now, so this is the ground
        // beneath it, and it keeps a line of its own only for the case where
        // the curtain is suppressed. That line no longer tells a first-time
        // joiner they are being reconnected to a room they have never been in.
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-wine-primary px-8 text-center">
          {!curtainActive && (
            <>
              <p className="text-lg font-semibold text-white">
                {rejoining ? 'Getting back to the room…' : 'Joining the room…'}
              </p>
              <p className="text-sm text-white/70">
                {rejoining
                  ? 'Catching up with everyone else. Your answers are safe.'
                  : 'Finding where everyone is up to.'}
              </p>
            </>
          )}
        </div>
      ) : shouldAnimate ? (
        <AnimatePresence mode="sync" initial={false}>
          <Motion.div
            key={motionKey}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0, transition: { duration: DUR.slow * 1.1, ease: EASE_BEZIER.softOut } }}
            exit={{ opacity: 0, y: -6, transition: { duration: 0.3, ease: [0.55, 0, 0.6, 1] } }}
            style={{ willChange: 'opacity, transform' }}
            className="absolute inset-0"
            onAnimationStart={() => {
              const t = Date.now();
              debugLog('🎬 OUTLET_ANIM_START', {
                motionKey,
                currentPath: location.pathname + location.search,
                expectedPath: lastPathRef.current,
                transitionCount: transitionCounterRef.current,
                stepIndex,
                time: t,
                iso: new Date(t).toISOString(),
              });
            }}
            onAnimationComplete={handleAnimComplete}
          >
            <Outlet />
          </Motion.div>
        </AnimatePresence>
      ) : (
        <div className="absolute inset-0">
          {debugLog('🚫 ANIM_DISABLED', {
            reason: {
              noFlow: !Array.isArray(flowSteps) || flowSteps.length === 0,
              prefetching: isPrefetchingNext,
              resumingPending:
                isResuming &&
                !(
                  typeof resumedStepIndex === 'number' &&
                  stepIndex === resumedStepIndex
                ),
            },
            path: location.pathname + location.search,
            stepIndex,
            isResuming,
            resumedStepIndex,
            isPrefetchingNext,
          })}
          <Outlet />
        </div>
      )}
    </div>
  );
}
